import { CircleLoader } from '@components/ui';
import { CookieHelper, GeneralHelper, APIHelper } from '@helpers';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
  AccountsApi,
  ApiApi,
  ApiJwtTokenApi,
  CustomQuestionnaireApi,
  RiskApi,
  RiskProfileApi,
  DashboardApi,
  GenerationInputsApi,
  DatasetPiplineApi,
  DatasetsApi,
  InferencersApi,
  QuestionnaireApi,
  AuxMetricsExcelExportApi,
} from '../api-client';

export interface Apis {
  api: ApiApi;
  accounts: AccountsApi;
  auth: ApiJwtTokenApi;
  customQuestionnaireApi: CustomQuestionnaireApi;
  riskApi: RiskApi;
  riskProfileApi: RiskProfileApi;
  dashboardApi: DashboardApi;
  generationInputsApi: GenerationInputsApi;
  datasetPiplineApi: DatasetPiplineApi;
  datasetsApi: DatasetsApi;
  inferencersApi: InferencersApi;
  questionnaireApi: QuestionnaireApi;
  auxMetricsExcelExportApi: AuxMetricsExcelExportApi;
}

function getApis(accessToken: string): Promise<Apis> {
  // get crsf token of current session and set globally for all API requests
  return APIHelper.createApiConfiguration(accessToken).then(config => APIHelper.createApisFromConfig(config));
}

export const ApiContext = React.createContext<
  (Apis & { setToken: React.Dispatch<React.SetStateAction<string>> }) | undefined
>(undefined);

export function useApi() {
  const api = useContext(ApiContext);
  if (!api) throw new Error('No provider for API');
  return api;
}

export const ApiProvider = ({ children }: { children: React.ReactNode }) => {
  const [apis, setApis] = useState<Apis>();
  const [accessToken, setToken] = useState(CookieHelper.get('access_token'));

  useEffect(() => {
    // generate API client
    getApis(accessToken).then(setApis);
  }, [accessToken]);

  useEffect(() => {
    if (apis) {
      GeneralHelper.runAsyncAssessments(apis.api);
    }
  }, [apis]);

  const value = useMemo(
    () => ({
      ...apis,
      setToken,
    }),
    [apis, setToken],
  );

  return apis ? <ApiContext.Provider value={value}>{children}</ApiContext.Provider> : <CircleLoader id="api-loader" />;
};
